import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { OperationsHistoryModal } from "@/v2/components/common/OperationsHistoryModal";
import LoaderComponent from "@/components/common/LoaderComponent";
import { IconButtonWithTooltip } from "@/components/ui/icon-button-tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  History, 
  Edit, 
  Trash2, 
  Archive, 
  ArchiveRestore,
  Copy, 
  Search,
  ArrowUpDown,
  Database,
  Filter,
  Loader2,
  FileSpreadsheet,
  ChevronDown,
  RotateCcw
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { usePatientGroups } from "@/hooks/activity/usePatientGroups";
import { usePatientGroupAuditLog } from "@/hooks/activity/usePatientGroupAuditLog";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { API_CONFIG } from "@/config/envConfig";

const PatientGroups = () => {
  const { patientGroups: apiPatientGroups, loading, error, refetch: refetchPatientGroups } = usePatientGroups();
  const [patientGroups, setPatientGroups] = useState<any[]>([]);
  const [entriesPerPage, setEntriesPerPage] = useState("10");
  const [searchBy, setSearchBy] = useState("groupName");
  const [searchValue, setSearchValue] = useState("");
  const [sortConfig, setSortConfig] = useState<{key: string, direction: 'asc' | 'desc'} | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedGroupForHistory, setSelectedGroupForHistory] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [groupToArchive, setGroupToArchive] = useState<string>("");
  const [isArchiving, setIsArchiving] = useState(false);
  const [isCurrentlyArchived, setIsCurrentlyArchived] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { token, user } = useAuth();

  // Fetch patient group audit logs
  const { auditLogs: patientGroupAuditLogs, loading: auditLogsLoading } = usePatientGroupAuditLog(selectedGroupForHistory);

  useEffect(() => {
    document.title = "Patient Groups | Admin";
  }, []);

  useEffect(() => {
    // Sort by patient_group_id descending to show latest first
    const sortedByLatest = [...apiPatientGroups].sort((a, b) => 
      b.patient_group_id - a.patient_group_id
    );
    setPatientGroups(sortedByLatest);
  }, [apiPatientGroups]);

  // Reset to first page when search or entries per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, entriesPerPage]);

  const handleCreateByEMRs = () => {
    navigate("/admin/patient-groups/create-by-emrs");
  };

  const handleCreateByFilters = () => {
    navigate("/admin/patient-groups/create-by-filters");
  };

  const handleCreateByExcel = () => {
    navigate("/admin/patient-groups/create-by-excel");
  };

  const handleViewArchivedGroups = () => {
    navigate("/admin/patient-groups/archived");
  };

  const handleHistory = (id: string) => {
    setSelectedGroupForHistory(parseInt(id));
    setIsHistoryModalOpen(true);
  };

  const handleEdit = (id: string) => {
    navigate(`/admin/patient-groups/edit/${id}`);
  };

  const handleDelete = (id: string) => {
    setGroupToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!user?.id || !token) {
      toast({
        title: "Authentication Error",
        description: "You must be logged in to delete a group.",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    try {
      const endpoint = `${API_CONFIG.API_BASE}/api/ngpep/${user.id}/patient-group/${groupToDelete}`;
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      toast({
        title: "Patient Group Deleted",
        description: "The patient group has been successfully deleted.",
      });
      // Refetch from API to get fresh data
      await refetchPatientGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete patient group.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setGroupToDelete("");
    }
  };

  const handleArchive = (id: string) => {
    const group = patientGroups.find(g => g.patient_group_id.toString() === id);
    setGroupToArchive(id);
    setIsCurrentlyArchived(group?.is_archived || false);
    setIsArchiveDialogOpen(true);
  };

  const confirmArchive = async () => {
    if (!user?.id || !token) {
      toast({
        title: "Authentication Error",
        description: `You must be logged in to ${isCurrentlyArchived ? 'unarchive' : 'archive'} a group.`,
        variant: "destructive",
      });
      return;
    }

    setIsArchiving(true);
    try {
      const endpoint = `${API_CONFIG.API_BASE}/api/ngpep/${user.id}/patient-group/${groupToArchive}`;
      
      const payload = {
        isArchived: !isCurrentlyArchived,
        patientIdsToAdd: [],
        patientIdsToRemove: []
      };

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      setPatientGroups(patientGroups.map(group => 
        group.patient_group_id.toString() === groupToArchive 
          ? { ...group, is_archived: !isCurrentlyArchived } 
          : group
      ));
      
      toast({
        title: `Patient Group ${isCurrentlyArchived ? 'Unarchived' : 'Archived'}`,
        description: `The patient group has been successfully ${isCurrentlyArchived ? 'unarchived' : 'archived'}.`,
      });
    } catch (error) {
      console.error(`Error ${isCurrentlyArchived ? 'unarchiving' : 'archiving'} group:`, error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : `Failed to ${isCurrentlyArchived ? 'unarchive' : 'archive'} patient group.`,
        variant: "destructive",
      });
    } finally {
      setIsArchiving(false);
      setIsArchiveDialogOpen(false);
      setGroupToArchive("");
      setIsCurrentlyArchived(false);
    }
  };

  const handleDuplicate = (id: string) => {
    navigate(`/admin/patient-groups/duplicate/${id}`);
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSearch = () => {
    setCurrentPage(1);
    if (!searchValue.trim()) {
      // Sort by patient_group_id descending to show latest first
      const sortedByLatest = [...apiPatientGroups].sort((a, b) => 
        b.patient_group_id - a.patient_group_id
      );
      setPatientGroups(sortedByLatest);
      return;
    }

    const filtered = apiPatientGroups.filter(group => {
      if (searchBy === 'groupName') {
        return group.group_name?.toLowerCase().includes(searchValue.toLowerCase());
      }
      if (searchBy === 'status') {
        const status = group.is_archived ? 'Archived' : 'Active';
        return status.toLowerCase().includes(searchValue.toLowerCase());
      }
      return false;
    });
    // Sort filtered results by patient_group_id descending
    const sortedFiltered = filtered.sort((a, b) => b.patient_group_id - a.patient_group_id);
    setPatientGroups(sortedFiltered);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'default';
      case 'Inactive':
        return 'secondary';
      case 'Archived':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const sortedGroups = useMemo(() => {
    if (!sortConfig) return patientGroups;

    return [...patientGroups].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortConfig.key === 'groupName') {
        aValue = a.group_name;
        bValue = b.group_name;
      } else if (sortConfig.key === 'totalPatients') {
        aValue = a.patient_count;
        bValue = b.patient_count;
      } else if (sortConfig.key === 'status') {
        aValue = a.is_archived ? 'Archived' : 'Active';
        bValue = b.is_archived ? 'Archived' : 'Active';
      } else {
        aValue = a[sortConfig.key as keyof typeof a];
        bValue = b[sortConfig.key as keyof typeof b];
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [patientGroups, sortConfig]);

  // Pagination calculations
  const totalEntries = sortedGroups.length;
  const entriesPerPageNum = parseInt(entriesPerPage);
  const totalPages = Math.ceil(totalEntries / entriesPerPageNum);
  const startIndex = (currentPage - 1) * entriesPerPageNum;
  const endIndex = Math.min(startIndex + entriesPerPageNum, totalEntries);
  const paginatedGroups = sortedGroups.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const isProcessing = isDeleting || isArchiving;

  return (
    <div className="space-y-6 relative">
      {isProcessing && (
        <LoaderComponent
          overlay
          size="lg"
          text={isDeleting ? 'Deleting Patient Group...' : isArchiving ? (isCurrentlyArchived ? 'Unarchiving Patient Group...' : 'Archiving Patient Group...') : 'Processing...'}
        />
      )}
      <header className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Patient Groups</h1>
            <p className="text-muted-foreground mt-2">Organize and manage patient groups</p>
          </div>
          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleViewArchivedGroups}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Archive className="h-4 w-4 mr-2" />
              Archived Groups
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-black px-6">
                  Create New Group
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 bg-background z-50">
                <DropdownMenuItem
                  onClick={handleCreateByEMRs}
                  className="cursor-pointer"
                >
                  <Database className="h-4 w-4 mr-2" />
                  Create New Group By EMRs
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleCreateByFilters}
                  className="cursor-pointer"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Create New Group By Filters
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleCreateByExcel}
                  className="cursor-pointer"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Create New Group By Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="flex gap-2 items-center">
              <Select value={searchBy} onValueChange={setSearchBy}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Search by..." />
                </SelectTrigger>
                <SelectContent className="z-50 bg-background border shadow-md">
                  <SelectItem value="groupName">Group Name</SelectItem>
                </SelectContent>
              </Select>
              
              <Input
                placeholder="Enter search value..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-64"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              
              <Button onClick={handleSearch}>
                <Search className="h-4 w-4" />
                Search
              </Button>
              {searchValue.trim() !== '' && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchValue('');
                    setCurrentPage(1);
                    const sortedByLatest = [...apiPatientGroups].sort((a, b) => 
                      b.patient_group_id - a.patient_group_id
                    );
                    setPatientGroups(sortedByLatest);
                  }}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>
          </div>
          {/* Entries per page selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Show</span>
          <Select value={entriesPerPage} onValueChange={setEntriesPerPage}>
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-50 bg-background border shadow-md">
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">entries</span>
        </div>
        </div>
      </header>

      <main className="space-y-4">
        {loading && (
          <LoaderComponent size="md" text="Loading patient groups..." />
        )}

        {error && (
          <div className="text-center py-8">
            <p className="text-destructive">Error loading patient groups: {error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Patient Groups Table */}
            <div id="table-admin-patient-groups-container" className="border rounded-lg">
              <Table id="table-admin-patient-groups">
                <TableHeader>
                  <TableRow id="table-admin-patient-groups-header-row">
                    <TableHead id="header-cell-group-name" className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('groupName')}>
                      <div className="flex items-center gap-2">
                        Group Name
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead id="header-cell-total-patients" className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('totalPatients')}>
                      <div className="flex items-center gap-2">
                        Total Patients
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead id="header-cell-status" className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('status')}>
                      <div className="flex items-center gap-2">
                        Status
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead id="header-cell-actions" className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody id="table-admin-patient-groups-body">
                  {paginatedGroups.map((group) => (
                    <TableRow key={group.patient_group_id} id={`row-patient-group-${group.patient_group_id}`}>
                      <TableCell id={`cell-patient-group-${group.patient_group_id}-name`} className="font-medium">{group.group_name}</TableCell>
                      <TableCell id={`cell-patient-group-${group.patient_group_id}-count`}>{group.patient_count}</TableCell>
                      <TableCell id={`cell-patient-group-${group.patient_group_id}-status`}>
                        <Badge variant={group.is_archived ? 'outline' : 'default'}>
                          {group.is_archived ? 'Archived' : 'Active'}
                        </Badge>
                      </TableCell>
                      <TableCell id={`cell-patient-group-${group.patient_group_id}-actions`}>
                        <div className="flex items-center gap-2 justify-end">
                          <IconButtonWithTooltip
                            id={`btn-patient-group-${group.patient_group_id}-history`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleHistory(group.patient_group_id.toString())}
                            className="text-blue-500 hover:text-blue-600"
                            tooltip="View History"
                            icon={<History className="h-4 w-4" />}
                          />
                          <IconButtonWithTooltip
                            id={`btn-patient-group-${group.patient_group_id}-edit`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(group.patient_group_id.toString())}
                            className="text-cyan-500 hover:text-cyan-600"
                            tooltip="Edit"
                            icon={<Edit className="h-4 w-4" />}
                          />
                          <IconButtonWithTooltip
                            id={`btn-patient-group-${group.patient_group_id}-delete`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(group.patient_group_id.toString())}
                            className="text-destructive hover:text-destructive"
                            tooltip="Delete"
                            icon={<Trash2 className="h-4 w-4" />}
                          />
                          <IconButtonWithTooltip
                            id={`btn-patient-group-${group.patient_group_id}-archive`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleArchive(group.patient_group_id.toString())}
                            className="text-purple-500 hover:text-purple-600"
                            tooltip={group.is_archived ? 'Unarchive' : 'Archive'}
                            icon={group.is_archived ? (
                              <ArchiveRestore className="h-4 w-4" />
                            ) : (
                              <Archive className="h-4 w-4" />
                            )}
                          />
                          <IconButtonWithTooltip
                            id={`btn-patient-group-${group.patient_group_id}-duplicate`}
                            variant="outline"
                            size="sm"
                            onClick={() => handleDuplicate(group.patient_group_id.toString())}
                            className="text-pink-500 hover:text-pink-600"
                            tooltip="Duplicate"
                            icon={<Copy className="h-4 w-4" />}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {sortedGroups.length === 0 && (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No patient groups found.</p>
              </div>
            )}
          </>
        )}
      </main>

      {!loading && !error && totalEntries > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground whitespace-nowrap">
            Showing {startIndex + 1} to {endIndex} of {totalEntries} entries
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => handlePageChange(currentPage - 1)}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              {getPageNumbers().map((page, index) => (
                <PaginationItem key={index}>
                  {page === '...' ? (
                    <span className="px-4">...</span>
                  ) : (
                    <PaginationLink
                      onClick={() => handlePageChange(page as number)}
                      className={
                        currentPage === page
                          ? 'bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer'
                          : 'hover:bg-muted cursor-pointer'
                      }
                    >
                      {page}
                    </PaginationLink>
                  )}
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext 
                  onClick={() => handlePageChange(currentPage + 1)}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the patient group.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isCurrentlyArchived ? 'Unarchive' : 'Archive'} Patient Group</AlertDialogTitle>
            <AlertDialogDescription>
              {isCurrentlyArchived 
                ? 'Are you sure you want to unarchive this patient group? It will be restored to the active groups list.'
                : 'Are you sure you want to archive this patient group? You can unarchive it later if needed.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmArchive();
              }}
              disabled={isArchiving}
            >
              {isArchiving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isCurrentlyArchived ? 'Unarchiving...' : 'Archiving...'}
                </>
              ) : (
                isCurrentlyArchived ? 'Unarchive' : 'Archive'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <OperationsHistoryModal
        open={isHistoryModalOpen}
        onOpenChange={setIsHistoryModalOpen}
        entityName={
          selectedGroupForHistory
            ? patientGroups.find(g => g.patient_group_id === selectedGroupForHistory)?.group_name || ""
            : ""
        }
        entityType="Group"
        auditLogs={patientGroupAuditLogs}
        loading={auditLogsLoading}
      />
    </div>
  );
};

export default PatientGroups;